import { z } from 'zod';
import { BooleanOptional, NumberOptional, StringOptional } from './schemaTypes';

import { LocationGroupType } from '@websky/graphql';

export const PassengerConfigSegmentSchema = z.object({
	arrival: z.union([z.literal('*'), z.string()]),
	departure: z.union([z.literal('*'), z.string()])
});

export const PassengerTypeConfigSchema = z.object({
	code: z.string().describe('Трехбуквенный код пассажира'),
	parentCode: StringOptional.describe('Основной код пассажира, например для "МЛА" (Молодежь) - это ADT (Взрослый)'),
	isAdult: BooleanOptional.describe('Является ли тип пассажира взрослым'),
	noAvailableWith: z.array(z.string()).optional().describe('Массив не совместимых code с данным'),
	defaultCount: NumberOptional.describe('Предзаданное количество'),
	onlyRoutes: z
		.array(z.array(PassengerConfigSegmentSchema))
		.optional()
		.describe(
			'Список маршрутов (IATA), на которых доступен данный тип пассажира. Поддерживается символ * - любой пункт'
		),
	disabledRoutes: z
		.array(z.array(PassengerConfigSegmentSchema))
		.optional()
		.describe(
			'Список маршрутов (IATA), на которых не доступен данный тип пассажира. Поддерживается символ * - любой пункт'
		),
	disabled: BooleanOptional,
	maxCountWithoutAdult: NumberOptional,
	blockPayByMiles: BooleanOptional.describe(
		'Если выбрана оплата милями, то типы пассажиров со значением true будут отфильтрованы из доступного для выбора списка. При переключении на оплату милями будет выведено popup окно с предупреждением.'
	),
	maxCount: NumberOptional,
	isIndependent: BooleanOptional,
	requiredWithOneOf: z.array(z.string()).optional(),
	modifyCount: NumberOptional,
	group: StringOptional.describe(
		'Если группа не задана то пассажир попадает в группу Standard\nЕсли групп больше одной то выводим их в селекторе пассажиров на форме поиска'
	)
});

const SelectableCurrencySchema = z.object({
	code: z.string(),
	symbol: StringOptional
});

const SearchFormLinkSchema = z.object({
	title: z.string(),
	url: z.string(),
	openInNewTab: BooleanOptional,
	icon: StringOptional,
	className: StringOptional
});

const LocationGroupTypesSchema = z.array(
	z.enum([
		LocationGroupType.AdditionalCities,
		LocationGroupType.Popular,
		LocationGroupType.Nearest,
		LocationGroupType.Country
	])
);

const LocationGroupsSchema = z.object({
	showAdditionalGroups: BooleanOptional,
	additionalGroupTypes: LocationGroupTypesSchema.optional()
});

const promoCodeWithAgreement = z.object({
	regex: z.instanceof(RegExp).or(z.string().transform(val => new RegExp(val))),
	code: z.string()
});

export const SearchFormSchema = z.object({
	redwingsForeignCardPaymentCheckbox: BooleanOptional.describe('@deprecated'),
	enableComplexRoute: BooleanOptional.describe('Возможность задавать сложный маршрут на форме поиска'),
	maxComplexRouteSegments: NumberOptional,
	mobileFormType: z.enum(['common', 'alternative']).optional(),
	engineURL: StringOptional.describe('Адрес страницы с модулем Engine (используется для FastSearch поиска)'),
	payByMiles: BooleanOptional.describe('Отображение переключателя оплатой милями на форме поиска'),
	showFfpBonusInfoInFares: BooleanOptional,
	requestCoordintates: z
		.object({
			desktop: BooleanOptional,
			mobile: BooleanOptional
		})
		.optional()
		.describe('Запрашивает местоположение для десктоп и мобильной версии'),
	maxPassengersCount: NumberOptional,
	passengersConfig: z
		.array(PassengerTypeConfigSchema)
		.optional()
		.describe('Конфиг для селектора Пассажиров в форме поиска, см PassengersConfig'),
	offerHotels: BooleanOptional.describe('Включение опции "Искать отели"'),
	offerHotelsDefaultState: BooleanOptional.describe('Начальное состояние переключателя "Искать отели"'),
	offerHotelsLink: StringOptional.describe('Ссылка на страницу отелей'),
	offerHotelsMainLink: StringOptional.describe(
		'Альтернативная ссылка на страницу отелей (кейс RM/WZ, для корректного перенаправления на страницу отелей)'
	),
	offerHotelsSupplier: z.enum(['OSTROVOK', 'ONETWOTRIP']).optional().describe('Поставщик отелей'),
	offerHotelsAdditionalId: StringOptional.describe(
		'Дополнительный ID для подставления в ссылку на перенаправления на отели (только для поставщика ONETWOTRIP)'
	),
	offerHotelsUtm: StringOptional.describe('UTM метка, которая подставляется в ссылку перенаправления на отели'),
	resultsURL: StringOptional.describe('@deprecated'),
	showPriceGraph: BooleanOptional.describe(
		'Отображать график цен в календаре в форме поиска, если настройка showPricesInDatepicker = false, график будет отображен вне зависимости от значения showPriceGraph'
	),
	showPricesInDatepicker: BooleanOptional.describe('Отображать цены в календаре в форме поиска'),
	proMode: BooleanOptional.describe('Включение функций LEO (WX Pro)'),
	selectableCurrencies: z
		.array(SelectableCurrencySchema)
		.optional()
		.describe('Задает валюты, доступные для выбора на форме поиска'),
	locationGroups: LocationGroupsSchema.optional(),
	startSearchButtonMode: z.enum(['text', 'image']).optional(),
	separatedDatepicker: BooleanOptional.describe(
		'При значении `true` поля `Откуда` и `Куда` всегда разделены. Выбор даты в одном поле закрывает календарь. Кнопки в календаре отсутствуют.'
	),
	promoCodesWithAgreement: z.array(promoCodeWithAgreement).optional(),
	additionalLinks: z.array(SearchFormLinkSchema).optional(),
	useFlightsSchedule: BooleanOptional,
	alwaysOpen: BooleanOptional.describe('При значении `true` форма поиска всегда открыта')
});
